import { useCallback, useEffect, useState } from "react";
import { fetchAdminEnrollments, confirmEnrollment, cancelEnrollment } from "./adminCourseService";
import "./adminCourses.css";

const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";

const STATUS_BADGE = {
  ACTIVE:          { cls: "ac-badge-green",  label: "Active"           },
  PENDING_PAYMENT: { cls: "ac-badge-orange", label: "Pending Payment"  },
  CANCELLED:       { cls: "ac-badge-red",    label: "Cancelled"        },
  INACTIVE:        { cls: "ac-badge-gray",   label: "Inactive"         },
};

const Toast = ({ msg, type, onClose }) => {
  useEffect(() => { const t = setTimeout(onClose, 3500); return () => clearTimeout(t); }, [onClose]);
  return (
    <div style={{
      position: "fixed", bottom: 24, right: 24, color: "#fff",
      background: type === "success" ? "#16a34a" : "#dc2626",
      padding: "12px 20px", borderRadius: 8, zIndex: 9999,
      boxShadow: "0 4px 16px rgba(0,0,0,0.18)", fontSize: "0.9rem",
    }}>
      {msg}
    </div>
  );
};

const AdminEnrollmentsPage = () => {
  const [rows, setRows]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState("");
  const [toast, setToast]         = useState(null);
  const [actionId, setActionId]   = useState(null); // enrollmentId being actioned
  const [pagination, setPagination] = useState({ page: 0, size: 20, totalPages: 0, totalElements: 0 });

  const [filters, setFilters] = useState({ status: "PENDING_PAYMENT", courseId: "", userId: "" });
  const [applied, setApplied] = useState(filters);

  const showToast = (msg, type = "success") => setToast({ msg, type });

  const load = useCallback(async (page = 0) => {
    setLoading(true); setError("");
    try {
      const params = { page, size: pagination.size };
      if (applied.status)   params.status   = applied.status;
      if (applied.courseId) params.courseId = applied.courseId;
      if (applied.userId)   params.userId   = applied.userId;
      const res = await fetchAdminEnrollments(params);
      const d = res.data;
      setRows(d.content || []);
      setPagination((p) => ({ ...p, page: d.number, totalPages: d.totalPages, totalElements: d.totalElements }));
    } catch { setError("Failed to load enrollments."); }
    finally  { setLoading(false); }
  }, [applied, pagination.size]);

  useEffect(() => { load(0); }, [applied]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleConfirm = async (enrollmentId) => {
    setActionId(enrollmentId);
    try {
      await confirmEnrollment(enrollmentId);
      showToast("Enrollment confirmed successfully.");
      load(pagination.page);
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed to confirm.", "error");
    } finally { setActionId(null); }
  };

  const handleCancel = async (enrollmentId) => {
    if (!window.confirm("Cancel this enrollment?")) return;
    setActionId(enrollmentId);
    try {
      await cancelEnrollment(enrollmentId);
      showToast("Enrollment cancelled.");
      load(pagination.page);
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed to cancel.", "error");
    } finally { setActionId(null); }
  };

  const handleApply = () => setApplied({ ...filters });
  const handleReset = () => {
    const e = { status: "PENDING_PAYMENT", courseId: "", userId: "" };
    setFilters(e); setApplied(e);
  };

  return (
    <div>
      {toast && <Toast msg={toast.msg} type={toast.type} onClose={() => setToast(null)} />}

      <div className="ac-header">
        <h2>Course Enrollments</h2>
      </div>

      {/* filters */}
      <div style={{
        background: "#fff", borderRadius: 10, padding: "1rem 1.25rem",
        boxShadow: "0 1px 4px rgba(0,0,0,0.07)", marginBottom: "1.25rem",
        display: "flex", gap: "0.75rem", flexWrap: "wrap", alignItems: "flex-end",
      }}>
        <div className="ac-form-group" style={{ margin: 0 }}>
          <label>Status</label>
          <select value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}>
            <option value="">All</option>
            <option value="PENDING_PAYMENT">Pending Payment</option>
            <option value="ACTIVE">Active</option>
            <option value="CANCELLED">Cancelled</option>
            <option value="INACTIVE">Inactive</option>
          </select>
        </div>
        <div className="ac-form-group" style={{ margin: 0 }}>
          <label>Course ID</label>
          <input type="number" placeholder="e.g. 4" value={filters.courseId} style={{ width: 90 }}
            onChange={(e) => setFilters((f) => ({ ...f, courseId: e.target.value }))} />
        </div>
        <div className="ac-form-group" style={{ margin: 0 }}>
          <label>User ID</label>
          <input type="number" placeholder="e.g. 180" value={filters.userId} style={{ width: 90 }}
            onChange={(e) => setFilters((f) => ({ ...f, userId: e.target.value }))} />
        </div>
        <div style={{ display: "flex", gap: "0.5rem" }}>
          <button className="ac-btn ac-btn-primary" onClick={handleApply}>Apply</button>
          <button className="ac-btn ac-btn-ghost"   onClick={handleReset}>Reset</button>
        </div>
      </div>

      {error && <div style={{ background: "#fee2e2", color: "#b91c1c", padding: "0.6rem 0.9rem", borderRadius: 6, marginBottom: "1rem", fontSize: "0.88rem" }}>{error}</div>}

      {loading ? (
        <div className="ac-loading">Loading enrollments…</div>
      ) : rows.length === 0 ? (
        <div className="ac-empty">No enrollments found.</div>
      ) : (
        <>
          <div style={{ fontSize: "0.82rem", color: "#6b7280", marginBottom: "0.5rem" }}>
            {pagination.totalElements} record{pagination.totalElements !== 1 ? "s" : ""}
          </div>
          <div className="ac-table-wrap">
            <table className="ac-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>User</th>
                  <th>Course / Batch</th>
                  <th>Status</th>
                  <th>Requested At</th>
                  <th>Enrolled At</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const badge = STATUS_BADGE[r.status] || { cls: "ac-badge-gray", label: r.status };
                  const busy  = actionId === r.enrollmentId;
                  return (
                    <tr key={r.enrollmentId}>
                      <td style={{ color: "#9ca3af", fontSize: "0.8rem" }}>{r.enrollmentId}</td>
                      <td>
                        <div style={{ fontWeight: 600, fontSize: "0.9rem" }}>{r.userName}</div>
                        <div style={{ fontSize: "0.78rem", color: "#6b7280" }}>{r.userEmail}</div>
                        {r.userPhone && <div style={{ fontSize: "0.78rem", color: "#6b7280" }}>{r.userPhone}</div>}
                      </td>
                      <td>
                        <div style={{ fontWeight: 600, fontSize: "0.88rem" }}>{r.courseTitle || r.courseName}</div>
                        <div style={{ fontSize: "0.78rem", color: "#6b7280" }}>{r.batchName}</div>
                      </td>
                      <td><span className={`ac-badge ${badge.cls}`}>{badge.label}</span></td>
                      <td style={{ fontSize: "0.82rem", color: "#6b7280" }}>{fmtDate(r.createdAt)}</td>
                      <td style={{ fontSize: "0.82rem", color: "#6b7280" }}>{fmtDate(r.enrolledAt)}</td>
                      <td>
                        <div style={{ display: "flex", gap: "0.4rem" }}>
                          {r.status === "PENDING_PAYMENT" && (
                            <button
                              className="ac-btn ac-btn-primary ac-btn-sm"
                              disabled={busy}
                              onClick={() => handleConfirm(r.enrollmentId)}
                            >
                              {busy ? "…" : "✓ Confirm"}
                            </button>
                          )}
                          {(r.status === "PENDING_PAYMENT" || r.status === "ACTIVE") && (
                            <button
                              className="ac-btn ac-btn-ghost ac-btn-sm"
                              disabled={busy}
                              onClick={() => handleCancel(r.enrollmentId)}
                              style={{ color: "#dc2626" }}
                            >
                              {busy ? "…" : "✕ Cancel"}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {pagination.totalPages > 1 && (
            <div style={{ display: "flex", gap: "0.5rem", justifyContent: "center", marginTop: "1rem", flexWrap: "wrap" }}>
              <button className="ac-btn ac-btn-ghost ac-btn-sm" disabled={pagination.page === 0} onClick={() => load(pagination.page - 1)}>← Prev</button>
              {Array.from({ length: pagination.totalPages }, (_, i) => (
                <button key={i} className={`ac-btn ac-btn-sm ${pagination.page === i ? "ac-btn-primary" : "ac-btn-ghost"}`} onClick={() => load(i)}>{i + 1}</button>
              ))}
              <button className="ac-btn ac-btn-ghost ac-btn-sm" disabled={pagination.page === pagination.totalPages - 1} onClick={() => load(pagination.page + 1)}>Next →</button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AdminEnrollmentsPage;
